import { FC, useState } from 'react';
import { toast } from 'react-hot-toast';
import { GuestWallet } from '../../wallets';
import { ClipboardIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../context/ThemeContext';

interface GuestModeLoginProps {
  onBack: () => void;
  onSuccess: (wallet: GuestWallet) => void;
  isLoading?: boolean;
  setIsLoading?: (loading: boolean) => void;
}

const GuestModeLogin: FC<GuestModeLoginProps> = ({ onBack, onSuccess }) => {
  const [publicKey, setPublicKey] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const handleLogin = async () => {
    if (!publicKey.trim()) {
      toast.error('Please enter a public key');
      return;
    }

    if (!apiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    setIsLoading(true);
    try {
      const wallet = new GuestWallet(publicKey, apiKey.trim());
      await wallet.connect(publicKey);
      onSuccess(wallet);
    } catch (error) {
      console.error('Failed to login with guest mode:', error);
      toast.error('Failed to login with guest mode. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setPublicKey(text);
    } catch (error) {
      console.error('Failed to paste from clipboard:', error);
      toast.error('Failed to paste from clipboard');
    }
  };

  const handlePasteApiKey = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setApiKey(text);
    } catch (error) {
      console.error('Failed to paste API key from clipboard:', error);
      toast.error('Failed to paste API key from clipboard');
    }
  };

  // Theme-aware colors
  const textPrimaryClass = isDarkMode ? 'text-white' : 'text-gray-900';
  const textTertiaryClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textMutedClass = isDarkMode ? 'text-gray-500' : 'text-[#666666]';
  const dividerClass = isDarkMode ? 'bg-[#292929]' : 'bg-gray-200';
  const inputBgClass = isDarkMode ? 'bg-gray-700' : 'bg-gray-100';
  const inputTextClass = isDarkMode ? 'text-gray-200' : 'text-gray-800';
  const inputPlaceholderClass = isDarkMode ? 'placeholder-gray-500' : 'placeholder-gray-500';
  const inputRingClass = isDarkMode ? 'focus:ring-gray-600' : 'focus:ring-gray-300';

  return (
    <div className="h-full grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-4 md:gap-7">
      <div className="flex flex-col justify-between">
        <div>
          <button 
            onClick={onBack}
            className="mb-6 flex items-center gap-2 group"
          >
            <svg width="9" height="15" viewBox="0 0 9 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.77979 1.14929L1.77979 7.14929L7.77978 13.1493" stroke="#00CC6A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className={`${textMutedClass} text-[14px] font-inter font-normal hover:text-[#00CC6A] transition-colors`}>Back</span>
          </button>

          <div>
            <h2 className={`${textTertiaryClass} text-[24px] mb-2 sm:-mb-1 font-normal opacity-40`}>Log in with</h2>
            <h1 
              className={`${textPrimaryClass} text-[32px] sm:text-[32px] leading-[160%] sm:leading-[140%] tracking-tight login-heading`}
            >
              Guest Mode
            </h1>
            <p className={`${textTertiaryClass} text-[14px] mt-4 mb-8 md:mb-0 font-normal font-inter`}>
              Explore public data for any account on the network
            </p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          <div>
            <div className="relative">
              <input
                type="text"
                id="publicKey"
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                placeholder="Wallet address"
                className={`w-full px-4 pr-12 py-4 ${inputBgClass} rounded-xl ${inputTextClass} ${inputPlaceholderClass} focus:outline-none focus:ring-1 ${inputRingClass} font-inter`}
                disabled={isLoading}
              />
              <button
                onClick={handlePaste}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#00CC6A] hover:opacity-90 transition-opacity"
              >
                <ClipboardIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div>
            <div className="relative">
              <input
                type="password"
                id="apiKey"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter API key for access"
                className={`w-full px-4 pr-12 py-4 ${inputBgClass} rounded-xl ${inputTextClass} ${inputPlaceholderClass} focus:outline-none focus:ring-1 ${inputRingClass} font-inter`}
                disabled={isLoading}
                required
              />
              <button
                onClick={handlePasteApiKey}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#00CC6A] hover:opacity-90 transition-opacity"
              >
                <ClipboardIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full py-4 bg-[#00CC6A] hover:bg-[#00B058] rounded-xl text-[#2B2B2B] font-semibold transition-colors flex items-center justify-center font-inter"
          >
            {isLoading ? 'Logging in...' : 'Log in as Guest'}
          </button>
        </div>
      </div>

      {/* Vertical Divider */}
      <div className="hidden md:flex flex-col items-center justify-center relative h-full">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`w-px h-full ${dividerClass}`}></div>
        </div>
        <div className="relative px-2 py-4">
        </div>
      </div>

      {/* Right Side - Disclaimer */}
      <div className="flex flex-col justify-end mt-4 md:mt-0">
        <p className={`${textMutedClass} text-[14px] font-normal font-inter leading-[120%] tracking-[-0.02em]`}>
          Guest mode enables you to view public data for a wallet but does not allow you to control the gateways and DERs belonging to the wallet.
        </p>
      </div>
    </div>
  );
};

export default GuestModeLogin; 